import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Term from "../../term/Term";
import styles from "../css/LearningScreen.module.css";
import { LearningType } from "../model/LearningType";
import LoadingScreen from "../../common/LoadingScreen";
import { useLocation } from "react-router-dom";
import Toast from "../../../components/ui/Toast";
import LearningOptions from "./LearningOptions";
import Flashcards from "./Flashcards";
import {
  getLearningSelectedGroupsFromLocalStorage,
  getLearningSelectedLevelFromLocalStroage,
  getLearningSortTypeFromLocalStorage,
} from "../../settings/util/learningSettings";
import { SortType } from "../../settings/model/SortType";
import { shuffle } from "lodash";
import MultipleChoice from "./MultipleChoice";
import { getFilteredTermsWithOptions } from "../../term/terms";
import Written from "./Written";
import { Status } from "../../common/Status";
import {
  deleteLearningContinue,
  finishLearning,
  updateLearningContinue,
} from "../util/learningAPI";
import LearningResult from "../model/LearningResult";
import LearningResultScreen from "./LearningResultScreen";
import { generateRandomSeed } from "../../common/numbers";
import { useTerms } from "../../term/useTerms";
import {
  getCurrentCount,
  getPageSize,
  getTermsUsingDefaultTerms,
  getTotalCount,
  isLastCard,
  isShowingPronunciationSelectView,
} from "../util/learning";
import { LearningMode } from "../model/LearningMode";
import LearningContinueResult from "../model/LearningContinueResult";
import { LearningContinueRequest } from "../model/LearningContinueRequest";
import { EnglishPronunciation } from "../../voice/util/voice";
import { getEnglishPronunciationFromLocalStorage } from "../../settings/util/voiceSettings";
import {
  updateTerm as updateTermFromAPI,
  updateTermMark as updateTermMarkFromAPI,
} from "../../term/termAPI";
import { appRoutes } from "../../../components/layout/AppShell";
import {
  CloseRounded,
  EditRounded,
  TuneRounded,
} from "@mui/icons-material";
import IconButton from "../../../components/ui/IconButton";
import TermEditScreen from "../../term/view/TermEditScreen";
import { useAppDispatch } from "../../../hook";
import { updatePartialTerms } from "../../term/termSlice";

interface LearningScreenProps {
  defaultTerms: Term[] | null;
  learningType: LearningType;
  learningMode: LearningMode;
  learningContinue: LearningContinueResult | null;
  historyId?: string;
}

export default function LearningScreen() {
  const navigate = useNavigate();
  const termsDispatch = useAppDispatch();

  const { t } = useTranslation();
  const [terms] = useTerms();
  const [filteredTerms, setFilteredTerms] = useState<Term[]>([]);
  const [learningTerms, setLearningTerms] = useState<Term[]>([]);
  const [learnedTerms, setLearnedTerms] = useState<Term[]>([]);
  const [correctTerms, setCorrectTerms] = useState<Term[]>([]);
  const [incorrectTerms, setIncorrectTerms] = useState<Term[]>([]);

  const [seed] = useState(generateRandomSeed());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const location = useLocation();
  const {
    defaultTerms,
    learningType,
    learningMode: initialLearningMode,
    learningContinue: initialLearningContinue,
  } = location.state as LearningScreenProps;
  const [learningMode, setLearningMode] = useState(initialLearningMode);
  const [learningContinue] = useState(
    initialLearningContinue
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [openLearningOptions, setOpenLearningOptions] = useState(false);
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  // 학습 중 단어를 편집하면 store 와 목록이 바뀌는데, 이때 학습 목록을 새로 만들면
  // 순서가 다시 섞이므로 편집으로 인한 변경은 한 번 건너뛴다.
  const skipTermsSyncRef = useRef(false);
  const skipLearningTermsUpdateRef = useRef(false);
  // 학습 목록의 순서는 한 번만 정한다. 구간을 마칠 때마다 결과가 store 에 반영되는데,
  // 이때 순서를 다시 정하면 현재 페이지 기준으로 잘라낸 다음 구간에 이미 학습한 단어가 섞인다.
  const isLearningOrderFixedRef = useRef(false);
  const [isReviewCurrentSection, setIsReviewCurrentSection] =
    useState<boolean>();

  const [currentPage, setCurrentPage] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTerm, setCurrentTerm] = useState<Term>();
  const [progressRatio, setProgressRatio] = useState(0.0);
  const [finishCount, setFinishCount] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [learningContinueInitialized, setLearningContinueInitialized] =
    useState(false);

  const [termsUpdateResultStatus, setTermsUpdateResultStatus] = useState(
    Status.Ready
  );

  const [learningResult, setLearningResult] = useState<LearningResult>();
  const [isShowPronunciationSelectView, setIsShowPronunciationSelectView] =
    useState<boolean>(false);

  const [englishPronunciation, setEnglishPronunciation] =
    useState<EnglishPronunciation>(EnglishPronunciation.america);

  useEffect(() => {
    const value = getEnglishPronunciationFromLocalStorage();
    setEnglishPronunciation(value);
  }, [isUpdating]);

  useEffect(() => {
    if (skipTermsSyncRef.current) {
      skipTermsSyncRef.current = false;
      return;
    }
    if (isLearningOrderFixedRef.current) {
      syncFilteredTermsWithLatestTerms();
      return;
    }
    makeFilteredTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms]);

  useEffect(() => {
    if (skipLearningTermsUpdateRef.current) {
      skipLearningTermsUpdateRef.current = false;
      return;
    }
    updateLearningTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTerms]);

  // 편집 창에서 타이핑할 때 학습 단축키(숫자 · Space · Enter 등)가 동작하지 않도록 막는다.
  // 학습 단축키는 window 에 등록되어 있으므로 document 에서 전파를 멈춘다.
  useEffect(() => {
    if (editingTerm === null) return;

    const blockLearningShortcuts = (event: KeyboardEvent) => {
      event.stopPropagation();
    };

    document.addEventListener("keydown", blockLearningShortcuts);
    return () => {
      document.removeEventListener("keydown", blockLearningShortcuts);
    };
  }, [editingTerm]);

  useEffect(() => {
    setIsUpdating(false);
    updateShowingPronunciationSelectView();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTerm]);

  useEffect(() => {
    updateCard();
    updateRatio();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningTerms]);

  useEffect(() => {
    updateCard();
    updateRatio();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  useEffect(() => {
    switch (termsUpdateResultStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        setFinishCount(finishCount + 1);
        setIsReviewCurrentSection(undefined);
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsUpdateResultStatus]);

  useEffect(() => {
    if (correctTerms.length === 0 && incorrectTerms.length === 0) return;
    nextCard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctTerms, incorrectTerms]);

  useEffect(() => {
    if (currentPage !== 0) {
      updateLearningTerms();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (isReviewCurrentSection === true && finishCount !== 0) {
      reviewCurrentSection();
    } else if (isReviewCurrentSection === false && finishCount !== 0) {
      continueNextSection();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewCurrentSection]);

  async function updateShowingPronunciationSelectView() {
    try {
      if (currentTerm === undefined) return;
      const isShow = await isShowingPronunciationSelectView(currentTerm);
      setIsShowPronunciationSelectView(isShow);
    } catch (error) {}
  }

  function getLearningTitle(): string {
    switch (learningType) {
      case LearningType.flashcards:
        return t("Flashcard");
      case LearningType.multiplechoice:
        return t("MultipleChoiceTitle");
      case LearningType.spellingQuiz:
        return t("SpellingQuiz");
    }
  }

  function handleOnClickOptionsButton() {
    setOpenLearningOptions(true);
  }

  function handleOnClickEditButton() {
    if (currentIndex >= learningTerms.length) return;
    setEditingTerm(learningTerms[currentIndex]);
  }

  async function saveEditedTerm(editedTerm: Term) {
    setIsLoading(true);
    try {
      await updateTermFromAPI(editedTerm);
      applyEditedTerm(editedTerm);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
    setIsLoading(false);
  }

  function applyEditedTerm(editedTerm: Term) {
    const editedValues = {
      term: editedTerm.term,
      definition: editedTerm.definition,
      memo: editedTerm.memo,
      pronunciation: editedTerm.pronunciation,
      group: editedTerm.group,
    };
    const replaceEditedTerm = (list: Term[]) => {
      list.forEach((t, index) => {
        if (t.id === editedTerm.id) {
          list[index] = { ...t, ...editedValues };
        }
      });
    };

    // 정답 · 오답 목록은 바뀌면 다음 카드로 넘어가므로 상태를 새로 만들지 않고 바로 고친다
    replaceEditedTerm(learningTerms);
    replaceEditedTerm(learnedTerms);
    replaceEditedTerm(correctTerms);
    replaceEditedTerm(incorrectTerms);

    skipLearningTermsUpdateRef.current = true;
    setFilteredTerms((prev) =>
      prev.map((t) => (t.id === editedTerm.id ? { ...t, ...editedValues } : t))
    );

    skipTermsSyncRef.current = true;
    termsDispatch(updatePartialTerms([{ id: editedTerm.id, ...editedValues }]));

    updateCard();
  }

  function handleOnClickExitButton() {
    // 학습을 시작한 화면(학습 탭 · 중요한 단어 목록)으로 돌아간다.
    // 새로고침 등으로 이전 기록이 없으면 학습 탭으로 이동한다.
    if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate(appRoutes.learn, { replace: true });
    }
  }

  function handleOnClickMarkButton() {
    try {
      const newMarked = !learningTerms[currentIndex].isMarked;
      learningTerms[currentIndex].isMarked = newMarked;
      updateTermMarkFromAPI(learningTerms[currentIndex], newMarked);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
    updateCard();
  }

  function handleOnClickCorrectButton() {
    learningTerms[currentIndex].isCorrectAnswer = true;
    insertAnsweredTerm(learningTerms[currentIndex]);
  }

  function handleOnClickIncorrectButton() {
    learningTerms[currentIndex].isCorrectAnswer = false;
    insertAnsweredTerm(learningTerms[currentIndex]);
  }

  function updateRatio() {
    const newCurrentCount = getCurrentCount(
      learningMode,
      currentPage,
      currentIndex,
      filteredTerms.length,
      learningContinue === null ? null : learningContinue.learningContinue
    );
    setCurrentCount(newCurrentCount);

    const newTotalCount = getTotalCount(
      learningMode,
      currentPage,
      learningTerms.length,
      learningContinue === null ? null : learningContinue.learningContinue
    );
    setTotalCount(newTotalCount);

    const newRatio = (currentCount / totalCount) * 100;
    setProgressRatio(newRatio);
  }

  function insertAnsweredTerm(term: Term) {
    if (term.isCorrectAnswer) {
      const newCorrectTerms = [...correctTerms];
      newCorrectTerms.push(term);
      setCorrectTerms(newCorrectTerms);
    } else {
      const newIncorrectTerms = [...incorrectTerms];
      newIncorrectTerms.push(term);
      setIncorrectTerms(newIncorrectTerms);
    }

    if (!learnedTerms.some((t) => t.id === term.id)) {
      const newLearnedTerms = [...learnedTerms];
      newLearnedTerms.push(term);
      setLearnedTerms(newLearnedTerms);
    } else {
      const newLearnedTerms = learnedTerms.map((t) =>
        t.id === term.id ? term : t
      );
      setLearnedTerms(newLearnedTerms);
    }
  }

  function nextCard() {
    setIsUpdating(true);
    const nextIndex = currentIndex + 1;
    if (nextIndex >= learningTerms.length) {
      finish();
    } else {
      setCurrentIndex(nextIndex);
    }
  }

  function updateCard() {
    if (currentIndex < learningTerms.length) {
      setCurrentTerm(
        (learningTerms[currentIndex] = {
          ...learningTerms[currentIndex],
          index: currentIndex,
        })
      );
    }
  }

  function updateLearningTerms() {
    let newTerms = filteredTerms;
    const pageSize = getPageSize(
      filteredTerms.length,
      learningContinue === null ? null : learningContinue.learningContinue
    );

    switch (learningMode) {
      case LearningMode.normal:
      case LearningMode.restartFromLearningHistory:
        const first = currentPage === 0 ? 0 : currentPage * pageSize;
        const end = Math.min(first + pageSize, filteredTerms.length);
        newTerms = [...filteredTerms.slice(first, end)];
        if (getLearningSortTypeFromLocalStorage() === SortType.random) {
          newTerms = shuffle(newTerms);
        }
        setLearningTerms(newTerms);
        break;
      case LearningMode.restartOnlyIncorrectTerms:
        newTerms = shuffle(filteredTerms);
        setLearningTerms(newTerms);
        break;
    }
  }

  function finish() {
    if (correctTerms.length !== 0 || incorrectTerms.length !== 0) {
      updateTermsResult();
    }
  }

  async function deleteLearningContinueIfNeeded(isLastCard: boolean) {
    if (!isLastCard) return;
    await deleteLearningContinue();
  }

  async function updateLearningContinueIfNeeded(
    learningMode: LearningMode,
    isLastCard: boolean
  ) {
    if (learningMode === LearningMode.normal && isLastCard === false) {
      const request = new LearningContinueRequest(
        filteredTerms.map((term) => term.id),
        // 이어하기 중에는 설정값이 아니라 실제로 학습한 구간 크기를 저장해야 다음 이어하기 위치가 맞는다.
        getPageSize(
          filteredTerms.length,
          learningContinue === null ? null : learningContinue.learningContinue
        ),
        currentIndex,
        currentPage + 1
      );

      try {
        await updateLearningContinue(request);
      } catch (error) {
        console.log(error);
      }
    }
  }

  async function updateTermsResult() {
    setTermsUpdateResultStatus(Status.Loading);
    try {
      const result = await finishLearning(
        correctTerms,
        incorrectTerms,
        learningType,
        learningMode
      );
      result.isLastCards = isLastCard(
        learnedTerms.length,
        filteredTerms.length
      );
      result.learningMode = learningMode;
      result.learnedTerms = learnedTerms;

      await updateLearningContinueIfNeeded(
        result.learningMode,
        result.isLastCards
      );
      setLearningResult(result);
      setTermsUpdateResultStatus(Status.Done);
      deleteLearningContinueIfNeeded(result.isLastCards);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermsUpdateResultStatus(Status.Ready);
    }
  }

  function resetLearningValues() {
    setCorrectTerms([]);
    setIncorrectTerms([]);
    setLearningTerms([]);
    setLearnedTerms([]);
    setCurrentIndex(0);
    setCurrentPage(0);
    setFilteredTerms([]);
    setLearningResult(undefined);
  }

  function restartLearn() {
    if (learningMode === LearningMode.restartOnlyIncorrectTerms) {
      setLearningMode(LearningMode.normal);
    }

    resetLearningValues();
    makeFilteredTerms();
  }

  function restartOnlyIncorrectTerms(terms: Term[]) {
    resetLearningValues();
    setFilteredTerms(terms);
    isLearningOrderFixedRef.current = true;
  }

  /** 순서는 그대로 두고 store 의 최신 단어 값만 반영한다. 현재 구간도 다시 만들지 않는다. */
  function syncFilteredTermsWithLatestTerms() {
    const latestTermsById = new Map(terms.map((term) => [term.id, term]));
    skipLearningTermsUpdateRef.current = true;
    setFilteredTerms((prev) =>
      prev.map((term) => {
        const latestTerm = latestTermsById.get(term.id);
        return latestTerm ?? term;
      })
    );
  }

  /** 학습 목록을 새로 정했으면 이후 store 변경으로 순서가 바뀌지 않도록 고정한다. */
  function applyNewFilteredTerms(newFilteredTerms: Term[]) {
    setFilteredTerms(newFilteredTerms);
    if (newFilteredTerms.length !== 0) {
      isLearningOrderFixedRef.current = true;
    }
  }

  function makeFilteredTerms() {
    if (learningContinue !== null) {
      const newFilteredTerms = [...learningContinue.terms];
      applyNewFilteredTerms(newFilteredTerms);

      if (learningContinueInitialized === false) {
        setLearningContinueInitialized(true);
        setCurrentPage(learningContinue.learningContinue.currentPage);

        const newLeanredTerms = [
          ...learningContinue.terms.slice(
            0,
            learningContinue.learningContinue.itemSize *
              learningContinue.learningContinue.currentPage
          ),
        ];
        setLearnedTerms(newLeanredTerms);
      }
    } else if (defaultTerms === null || defaultTerms === undefined) {
      const newFilteredTerms = getFilteredTermsWithOptions(
        terms,
        seed,
        getLearningSortTypeFromLocalStorage(),
        getLearningSelectedGroupsFromLocalStorage(),
        getLearningSelectedLevelFromLocalStroage()
      );
      applyNewFilteredTerms(newFilteredTerms);
    } else {
      const newFilteredTerms = getTermsUsingDefaultTerms(defaultTerms, terms);
      applyNewFilteredTerms(newFilteredTerms);
    }
  }

  function continueNextSection() {
    setLearningResult(undefined);
    setCorrectTerms([]);
    setIncorrectTerms([]);
    setCurrentIndex(0);

    // 실제 학습 완료된 수 기준으로 다음 페이지를 계산하되,
    // 학습한 단어 수가 모자라도 같은 구간이 반복되지 않도록 최소 한 페이지는 넘어간다.
    const pageSize = getPageSize(
      filteredTerms.length,
      learningContinue === null ? null : learningContinue.learningContinue
    );
    const nextPage = Math.max(
      currentPage + 1,
      Math.floor(learnedTerms.length / pageSize)
    );
    setCurrentPage(nextPage);
  }

  function reviewCurrentSection() {
    setLearningResult(undefined);
    setCorrectTerms([]);
    setIncorrectTerms([]);
    setCurrentIndex(0);
    updateLearningTerms();
    updateCard();
  }

  function reviewCurrentSectionOnlyIncorrectTerms(incorrectTerms: Term[]) {
    setLearningResult(undefined);
    setCorrectTerms([]);
    setIncorrectTerms([]);
    setCurrentIndex(0);
    setLearningTerms(incorrectTerms);
    updateCard();
  }

  const prevAnswersRef = useRef<Term[] | null>(null);
  const prevAnswerCardKeyRef = useRef<string | null>(null);

  function makeAnswers(): Term[] {
    if (!currentTerm) return [];

    // 같은 카드에서는 다시 렌더링되어도 보기를 새로 뽑지 않는다.
    // 단어가 편집될 수 있으므로 순서만 유지하고 단어는 최신 값을 쓴다.
    const cardKey = `${currentTerm.id}|${currentTerm.index}`;
    if (prevAnswerCardKeyRef.current === cardKey && prevAnswersRef.current) {
      const latestAnswers = prevAnswersRef.current.map((answer) =>
        answer.id === currentTerm.id
          ? currentTerm
          : terms.find((t) => t.id === answer.id) ?? answer
      );
      prevAnswersRef.current = latestAnswers;
      return latestAnswers;
    }

    const newTerms = getFilteredTermsWithOptions(
      terms,
      undefined,
      SortType.random,
      getLearningSelectedGroupsFromLocalStorage()
    );

    const correctTermIds = new Set(correctTerms.map((t) => t.id));

    const candidates = newTerms
      .filter((t) => !correctTermIds.has(t.id))
      .filter((t) => t.id !== currentTerm.id);

    const picked = candidates.slice(0, 3);
    const shuffled = shuffle([...picked, currentTerm]);

    prevAnswerCardKeyRef.current = cardKey;
    prevAnswersRef.current = shuffled;

    return shuffled;
  }

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <div className={styles.topContainer}>
            <IconButton
              aria-label={t("Quit")}
              icon={<CloseRounded />}
              onClick={() => handleOnClickExitButton()}
            />
            <div className={styles.titleBlock}>
              <span className={styles.learningTypeText}>
                {getLearningTitle()}
              </span>
              <span className={styles.countText}>
                {learningResult === undefined &&
                  `${currentCount} / ${totalCount}`}
                {learningResult !== undefined &&
                  `${currentCount} / ${filteredTerms.length}`}
              </span>
            </div>
            <div className={styles.topRightButtons}>
              {learningResult === undefined && currentTerm !== undefined && (
                <IconButton
                  aria-label={t("Edit")}
                  icon={<EditRounded />}
                  onClick={() => handleOnClickEditButton()}
                />
              )}
              <IconButton
                aria-label={t("Options")}
                icon={<TuneRounded />}
                onClick={() => handleOnClickOptionsButton()}
              />
            </div>
          </div>
          {learningResult !== undefined && (
            <LearningResultScreen
              open={learningResult !== undefined}
              result={learningResult}
              onClickContinueButton={() => continueNextSection()}
              onClickRestartButton={() => restartLearn()}
              onClickRestartOnlyIncorrectButton={restartOnlyIncorrectTerms}
              onClickReviewCurrentSectionButton={() =>
                setIsReviewCurrentSection(true)
              }
              onClickReviewCurrentSectionOnlyIncorrectButton={
                reviewCurrentSectionOnlyIncorrectTerms
              }
              onTermDeleted={(deletedIds: string[]) => {
                setFilteredTerms((prev) =>
                  prev.filter((t) => !deletedIds.includes(t.id))
                );
                setLearningTerms((prev) =>
                  prev.filter((t) => !deletedIds.includes(t.id))
                );
                setCorrectTerms((prev) =>
                  prev.filter((t) => !deletedIds.includes(t.id))
                );
                setIncorrectTerms((prev) =>
                  prev.filter((t) => !deletedIds.includes(t.id))
                );
              }}
            />
          )}
          {learningResult === undefined && (
            <div className={styles.learningContainer}>
              <div
                className={styles.progress}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progressRatio)}
              >
                <div
                  className={styles.progressBar}
                  style={{ width: `${progressRatio}%` }}
                />
              </div>
              {learningType === LearningType.flashcards &&
                currentTerm !== undefined &&
                isUpdating === false && (
                  <Flashcards
                    term={currentTerm}
                    englishPronunciation={englishPronunciation}
                    showingPronunciationSelectView={
                      isShowPronunciationSelectView
                    }
                    onUpdateMark={handleOnClickMarkButton}
                    onCorrect={handleOnClickCorrectButton}
                    onIncorrect={handleOnClickIncorrectButton}
                    onSpeakError={(error: string) => {
                      setToastMessage(error);
                    }}
                    onClickPronunciationSelectButton={(
                      pronunciation: EnglishPronunciation
                    ) => {
                      setEnglishPronunciation(pronunciation);
                    }}
                  />
                )}
              {learningType === LearningType.multiplechoice &&
                currentTerm !== undefined &&
                isUpdating === false && (
                  <MultipleChoice
                    term={currentTerm}
                    answers={makeAnswers()}
                    englishPronunciation={englishPronunciation}
                    showingPronunciationSelectView={
                      isShowPronunciationSelectView
                    }
                    isPaused={
                      isLoading === true ||
                      openLearningOptions === true ||
                      editingTerm !== null
                    }
                    onClickMarkButton={handleOnClickMarkButton}
                    onCorrect={handleOnClickCorrectButton}
                    onIncorrect={handleOnClickIncorrectButton}
                    onSpeakError={(error: string) => {
                      setToastMessage(error);
                    }}
                    onClickPronunciationSelectButton={(
                      pronunciation: EnglishPronunciation
                    ) => {
                      setEnglishPronunciation(pronunciation);
                    }}
                  />
                )}
              {learningType === LearningType.spellingQuiz &&
                currentTerm !== undefined &&
                isUpdating === false && (
                  <Written
                    term={currentTerm}
                    englishPronunciation={englishPronunciation}
                    showingPronunciationSelectView={
                      isShowPronunciationSelectView
                    }
                    onClickMarkButton={handleOnClickMarkButton}
                    onClickPronunciationSelectButton={(
                      pronunciation: EnglishPronunciation
                    ) => {
                      setEnglishPronunciation(pronunciation);
                    }}
                    onCorrect={handleOnClickCorrectButton}
                    onIncorrect={handleOnClickIncorrectButton}
                    onSpeakError={(error: string) => {
                      setToastMessage(error);
                    }}
                  />
                )}
            </div>
          )}
        </div>
        <LearningOptions
          open={openLearningOptions === true}
          terms={[]}
          type={learningType}
          learningContinue={null}
          mode="inLearning"
          onUpdateSelectedGroups={() => {}}
          onUpdateSelectedLevels={() => {}}
          onUpdateSortType={() => {}}
          onUpdateShowingPronunciationSelectView={() => {
            updateShowingPronunciationSelectView();
          }}
          onClickStartButton={() => {}}
          onClickRestartButton={() => restartLearn()}
          onClose={() => {
            document.body.style.overflow = "auto";
            setOpenLearningOptions(false);
          }}
        />
        {editingTerm !== null && (
          <TermEditScreen
            open={true}
            term={editingTerm}
            handleOnSave={(term: Term) => saveEditedTerm(term)}
            handleOnClose={() => setEditingTerm(null)}
          />
        )}
        <Toast
          open={toastMessage !== null}
          onClose={() => {
            setToastMessage(null);
          }}
          message={toastMessage}
        />
        {isLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
